import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import { fetchViewWorkOrderById } from '../../slices/work-order';
import { TOLERANCE_OPTIONS } from '../../constants/toleranceOptions';
import logo from '../../Images/logo.svg';
import { Col, Row } from 'antd';

const PrintWorkOrder2 = () => {
  const contentRef = useRef();
  const { id } = useParams();
  const dispatch = useDispatch();
  const [workOrderData, setWorkOrderData] = useState(null);
  const { user: currentUser } = useSelector(state => state.auth);

  const fetchData = async () => {
    try {
      const response = await dispatch(fetchViewWorkOrderById({ id })).unwrap();
      setWorkOrderData(response?.results?.data?.[0] || null);
    } catch (error) {
      console.error('Failed to fetch work order:', error);
    }
  };

  useEffect(() => {
    if (id) {
      fetchData();
    }
  }, [id]);


  const hasSurfaceTreatment = workOrderData?.work_order_details?.some(item =>
    Array.isArray(item?.jobwork) && item.jobwork.includes('Surface treatment')
  ) || false;
  const hasAnodising = workOrderData?.work_order_details?.some(
    item => Array.isArray(item?.jobwork) && item.jobwork.includes('Surface treatment') && item.anodising
  ) || false;
  const hasPowderCoating = workOrderData?.work_order_details?.some(
    item => Array.isArray(item?.jobwork) && item.jobwork.includes('Surface treatment') && item.powder_coating
  ) || false;
  const hasPVDF = workOrderData?.work_order_details?.some(
    item => Array.isArray(item?.jobwork) && item.jobwork.includes('Surface treatment') && item.pvdf
  ) || false;
  const hasEngineering = workOrderData?.work_order_details?.some(
    item => Array.isArray(item?.jobwork) && item.jobwork.includes('Engineering')
  ) || false;
  const hasCutting = workOrderData?.work_order_details?.some(
    item => Array.isArray(item?.jobwork) && item.jobwork.includes('Engineering') && item.cutting
  ) || false;
  const hasMachinings = workOrderData?.work_order_details?.some(
    item => Array.isArray(item?.jobwork) && item.jobwork.includes('Engineering') && item.machinings
  ) || false;
  const hasDeburring = workOrderData?.work_order_details?.some(
    item => Array.isArray(item?.jobwork) && item.jobwork.includes('Engineering') && item.deburring
  ) || false;
  const hasLaserMarking = workOrderData?.work_order_details?.some(
    item => Array.isArray(item?.jobwork) && item.jobwork.includes('Laser marking')
  ) || false;
  const hasLaserDescription = workOrderData?.work_order_details?.some(
    item => Array.isArray(item?.jobwork) && item.jobwork.includes('Laser marking') && item.laser_marking_description
  ) || false;
  const hasLaserPrice = workOrderData?.work_order_details?.some(
    item => Array.isArray(item?.jobwork) && item.jobwork.includes('Laser marking') && item.laser_marking_price
  ) || false;
  const hasMillFinish = workOrderData?.work_order_details?.some(
    item => Array.isArray(item?.jobwork) && item.jobwork.includes('Mill Finish')
  ) || false;
  const hasOutSource = workOrderData?.work_order_details?.some(
    item => Array.isArray(item?.jobwork) && item.jobwork.includes('Out Source')
  ) || false;
  const hasThermalBreak = workOrderData?.work_order_details?.some(
    item => Array.isArray(item?.jobwork) && item.jobwork.includes('Thermal Break')
  ) || false;
  const hasOnlyMillFinish = workOrderData?.work_order_details?.every(item => {
    return (
      Array.isArray(item.jobwork) &&
      item.jobwork.length === 1 &&
      item.jobwork[0] === 'Mill Finish'
    );
  });
  const showJobWorkColumn = workOrderData?.work_order_details?.some(item => {
    if (!Array.isArray(item?.jobwork)) return false;
    if (item.jobwork.length === 1 && item.jobwork.includes('Mill Finish')) return false;
    return item.jobwork.length > 0;
  }) || false;

  const getSurfaceTreatmentChildren = (title) => {
    const children = [];
    if (hasAnodising) {
      children.push({
        title: 'Anodising Description',
        dataIndex: 'anodising_description',
        key: 'anodising_description',
        align: 'center',
        width: 70,
        render: (_, record) =>
          Array.isArray(record?.jobwork) && record.jobwork.includes('Surface treatment') && record.anodising ? _ : '-',
      });
      if (title === 'ACCOUNT/SALES COPY') {
        children.push({
          title: 'Anodising Price',
          dataIndex: 'anodising_price',
          key: 'anodising_price',
          align: 'center',
          width: 60,
          render: (_, record) =>
            Array.isArray(record?.jobwork) && record.jobwork.includes('Surface treatment') && record.anodising ? _ : '-',
        });
      }
    }
    if (hasPowderCoating) {
      children.push({
        title: 'Powder Coating Description',
        dataIndex: 'powder_coating_description',
        key: 'powder_coating_description',
        align: 'center',
        width: 70,
        render: (_, record) =>
          Array.isArray(record?.jobwork) && record.jobwork.includes('Surface treatment') && record.powder_coating ? _ : '-',
      });
      if (title === 'ACCOUNT/SALES COPY') {
        children.push({
          title: 'Powder Coating Price',
          dataIndex: 'powder_coating_price',
          key: 'powder_coating_price',
          align: 'center',
          width: 60,
          render: (_, record) =>
            Array.isArray(record?.jobwork) && record.jobwork.includes('Surface treatment') && record.powder_coating ? _ : '-',
        });
      }
    }
    if (hasPVDF) {
      children.push({
        title: 'PVDF Description',
        dataIndex: 'pvdf_description',
        key: 'pvdf_description',
        align: 'center',
        width: 60,
        render: (_, record) =>
          Array.isArray(record?.jobwork) && record.jobwork.includes('Surface treatment') && record.pvdf ? _ : '-',
      });
      if (title === 'ACCOUNT/SALES COPY') {
        children.push({
          title: 'PVDF Price',
          dataIndex: 'pvdf_price',
          key: 'pvdf_price',
          align: 'center',
          width: 55,
          render: (_, record) =>
            Array.isArray(record?.jobwork) && record.jobwork.includes('Surface treatment') && record.pvdf ? _ : '-',
        });
      }
    }
    return children;
  };

  const getEngineeringChildren = (title) => {
    const children = [];
    if (hasCutting) {
      children.push({
        title: 'Cutting',
        dataIndex: 'cutting',
        key: 'cutting',
        align: 'center',
        width: 60,
        render: (_, record) =>
          Array.isArray(record?.jobwork) && record.jobwork.includes('Engineering') && record.cutting
            ? (title === 'ACCOUNT/SALES COPY' ? record.cutting_price : 'Yes')
            : '-',
      });
    }
    if (hasMachinings) {
      children.push({
        title: 'Machinings',
        dataIndex: 'machinings',
        key: 'machinings',
        align: 'center',
        width: 60,
        render: (_, record) =>
          Array.isArray(record?.jobwork) && record.jobwork.includes('Engineering') && record.machinings
            ? (title === 'ACCOUNT/SALES COPY' ? record.machinings_price : 'Yes')
            : '-',
      });
    }
    if (hasDeburring) {
      children.push({
        title: 'Deburring',
        dataIndex: 'deburring',
        key: 'deburring',
        align: 'center',
        width: 60,
        render: (_, record) =>
          Array.isArray(record?.jobwork) && record.jobwork.includes('Engineering') && record.deburring
            ? (title === 'ACCOUNT/SALES COPY' ? record.deburring_price : 'Yes')
            : '-',
      });
    }
    return children;
  };

  const getLaserMarkingChildren = (title) => {
    const children = [];
    if (hasLaserDescription) {
      children.push({
        title: 'Laser Marking Description',
        dataIndex: 'laser_marking_description',
        key: 'laser_marking_description',
        align: 'center',
        width: 60,
        render: (_, record) =>
          Array.isArray(record?.jobwork) && record.jobwork.includes('Laser marking') && record.laser_marking_description ? _ : '-',
      });
    }
    if (hasLaserPrice && title === 'ACCOUNT/SALES COPY') {
      children.push({
        title: 'Laser Marking Price',
        dataIndex: 'laser_marking_price',
        key: 'laser_marking_price',
        align: 'center',
        width: 60,
        render: (_, record) =>
          Array.isArray(record?.jobwork) && record.jobwork.includes('Laser marking') && record.laser_marking_price ? _ : '-',
      });
    }
    return children;
  };

  const isFixedPrice = workOrderData?.nalco_rate?.toLowerCase() === 'fixed';

  const getDimensions = record => {
    if (!record?.die_profile) return 'N/A';
    const dimensions = [
      record.die_profile.dimension1,
      record.die_profile.dimension2,
      record.die_profile.dimension3,
      record.die_profile.dimension4,
    ]
      .filter(dim => dim && dim !== '0.00')
      .map(dim => Number(dim)?.toFixed(2))
      .join(' X ') + ' MM';
    return dimensions || 'N/A';
  };

  const getAlloyTemper = record => {
    const alloyName = record?.alloy?.name || '';
    const alloyCode = record?.alloy?.color_code || '';
    const temperName = record?.temper?.name || '';
    const temperCode = record?.temper?.code || '';
    return `${alloyName} (${alloyCode}) / ${temperName} (${temperCode})` || 'N/A';
  };

  const getPrice = record => {
    const isFixed = workOrderData?.nalco_rate?.toLowerCase() === 'fixed';
    const isVariable = workOrderData?.nalco_rate?.toLowerCase() === 'variable';
    const conversion = record?.conversion ?? '0.00';
    if (isFixed) return `${record?.nalco_rate ?? '0.00'}+${conversion}`;
    if (isVariable) return `Variable+${conversion}`;
    return '0.00';
  };

  const getJobWorkOrder = () => {
    const jobWorkOrder = [];
    const seenJobWorks = new Set();
    workOrderData?.work_order_details?.forEach(item => {
      if (Array.isArray(item.jobwork)) {
        item.jobwork.forEach(job => {
          if (!seenJobWorks.has(job)) {
            jobWorkOrder.push(job);
            seenJobWorks.add(job);
          }
        });
      }
    });
    return jobWorkOrder;
  };

  const getJobWorkColumns = (title) => {
    const jobWorkOrder = getJobWorkOrder();
    const columns = [];
    jobWorkOrder.forEach(jobType => {
      switch (jobType) {
        case 'Surface treatment':
          if (hasSurfaceTreatment) {
            columns.push(...getSurfaceTreatmentChildren(title));
          }
          break;
        case 'Engineering':
          if (hasEngineering) {
            columns.push(...getEngineeringChildren(title));
          }
          break;
        case 'Laser marking':
          if (hasLaserMarking) {
            columns.push(...getLaserMarkingChildren(title));
          }
          break;
        case 'Mill Finish':
          if (hasMillFinish) {
            columns.push({
              title: 'Mill Finish',
              dataIndex: 'mill_finish',
              key: 'mill_finish',
              align: 'center',
              width: 60,
              render: (_, record) =>
                Array.isArray(record?.jobwork) && record.jobwork.includes('Mill Finish') ? 'Yes' : '-',
            });
          }
          break;
        case 'Out Source':
          if (hasOutSource) {
            columns.push({
              title: 'Out Source',
              dataIndex: 'out_source',
              key: 'out_source',
              align: 'center',
              width: 60,
              render: (_, record) =>
                Array.isArray(record?.jobwork) && record.jobwork.includes('Out Source')
                  ? record.out_source === true
                    ? 'Yes'
                    : record.out_source === false
                      ? 'No'
                      : '-'
                  : '-',
            });
          }
          break;
        case 'Thermal Break':
          if (hasThermalBreak) {
            columns.push({
              title: 'Thermal Break',
              dataIndex: 'thermal_break',
              key: 'thermal_break',
              align: 'center',
              width: 60,
              render: (_, record) =>
                Array.isArray(record?.jobwork) && record.jobwork.includes('Thermal Break') ? 'Yes' : '-',
            });
          }
          break;
        default:
          break;
      }
    });
    return columns;
  };

  const getJobWorkHeaderStructure = (title) => {
    const jobWorkOrder = getJobWorkOrder();
    const structure = [];
    jobWorkOrder.forEach(jobType => {
      switch (jobType) {
        case 'Surface treatment':
          if (hasSurfaceTreatment) {
            structure.push({
              type: 'group',
              title: 'Surface Treatment',
              colspan: getSurfaceTreatmentChildren(title).length,
              children: getSurfaceTreatmentChildren(title).map(child => ({
                type: 'column',
                title: child.title,
                key: child.key
              }))
            });
          }
          break;
        case 'Engineering':
          if (hasEngineering) {
            structure.push({
              type: 'group',
              title: 'Engineering',
              colspan: getEngineeringChildren(title).length,
              children: getEngineeringChildren(title).map(child => ({
                type: 'column',
                title: child.title,
                key: child.key
              }))
            });
          }
          break;
        case 'Laser marking':
          if (hasLaserMarking) {
            structure.push({
              type: 'group',
              title: 'Laser Marking',
              colspan: getLaserMarkingChildren(title).length,
              children: getLaserMarkingChildren(title).map(child => ({
                type: 'column',
                title: child.title,
                key: child.key
              }))
            });
          }
          break;
        case 'Mill Finish':
          if (hasMillFinish) {
            structure.push({
              type: 'column',
              title: 'Mill Finish',
              key: 'mill_finish'
            });
          }
          break;
        case 'Out Source':
          if (hasOutSource) {
            structure.push({
              type: 'column',
              title: 'Out Source',
              key: 'out_source'
            });
          }
          break;
        case 'Thermal Break':
          if (hasThermalBreak) {
            structure.push({
              type: 'column',
              title: 'Thermal Break',
              key: 'thermal_break'
            });
          }
          break;
        default:
          break;
      }
    });
    return structure;
  };

  const renderCopy = (title, includePriceColumns = false) => {
    const jobWorkColumns = getJobWorkColumns(title);
    const jobWorkStructure = getJobWorkHeaderStructure(title);
    const baseColumns = 11;
    const totalColumns = baseColumns + jobWorkColumns.length + (includePriceColumns ? 5 : 0);
    const totalRowFirstColSpan = 6;

    return (
      <div className="main-container">
        <Row justify="space-between" align="middle" style={{ marginBottom: '10px', marginTop: '5px' }}>
          <Col span={24} className="logo-section" style={{ textAlign: 'right' }}>
            <img src={logo} alt="Company Logo" style={{ maxWidth: '180px' }} />
          </Col>
        </Row>
        <Row justify="space-between" align="middle" style={{ marginBottom: '10px' }}>
          <Col span={24}>
            <div style={{ textAlign: 'center' }}>
              <h3 style={{ fontSize: '2rem', color: '#000000', fontWeight: 500 }} className='main-heading'>WORK ORDER</h3>
              <span className="copy-label">{title}</span>
            </div>
          </Col>
        </Row>
        <div className="info-section">
          <div className="info-left">
            <table className="info-table">
              <tbody>
                <tr>
                  <td>
                    <strong>{workOrderData?.bill_to?.customer_name || 'N/A'}</strong><br />
                    {[
                      workOrderData?.bill_to?.factory_address?.shop,
                      workOrderData?.bill_to?.factory_address?.area,
                      workOrderData?.bill_to?.factory_address?.landmark,
                      workOrderData?.bill_to?.factory_address?.city,
                      workOrderData?.bill_to?.factory_address?.state,
                      workOrderData?.bill_to?.factory_address?.pin_code,
                    ]
                      .filter(part => part && part.toLowerCase() !== 'na' && part.trim() !== '')
                      .join(', ') || 'N/A'}
                  </td>
                </tr>
                <tr>
                  <td className=""> <span className='info-label'>Delivery Party :</span> <br /> {workOrderData?.ship_to?.customer_name || 'N/A'}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="info-right">
            <table className="info-table">
              <tbody>
                <tr>
                  <td className="info-label">Order Date :</td>
                  <td>{workOrderData?.order_date ? dayjs(workOrderData.order_date).format('DD-MM-YYYY') : 'N/A'}</td>
                </tr>
                <tr>
                  <td className="info-label">Order Number :</td>
                  <td>{workOrderData?.order_no || 'N/A'}</td>
                </tr>
                <tr>
                  <td className="info-label">Purchase Order Date :</td>
                  <td>{workOrderData?.purchase_order_date ? dayjs(workOrderData.purchase_order_date).format('DD-MM-YYYY') : 'N/A'}</td>
                </tr>
                <tr>
                  <td className="info-label">Purchase Order Number :</td>
                  <td>{workOrderData?.purchase_order_no || 'N/A'}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        <table className="main-table table-bordered">
          <thead>
            <tr>
              <th rowSpan="3">SR NO.</th>
              <th rowSpan="3">SECTION NO.</th>
              <th rowSpan="3">IMAGE</th>
              <th rowSpan="3">DESCRIPTION</th>
              <th rowSpan="3">WT RANGE</th>
              <th rowSpan="3">LENGTH</th>
              <th colSpan="2">PIECES</th>
              <th colSpan="2">WEIGHT (kg.)</th>
              <th rowSpan="3">ALLOY/TEMP-GRADE</th>
              {showJobWorkColumn && (
                <th colSpan={jobWorkColumns.length}>Job Work</th>
              )}
              {['ACCOUNT/SALES COPY'].includes(title) && (
                <>
                  <th rowSpan="3">PRICE/KG</th>
                  <th rowSpan="3">PACKING CHARGES</th>
                </>
              )}
              {includePriceColumns && (
                <>
                  <th rowSpan="3">AMOUNT (Rs.)</th>
                  <th rowSpan="3">IGST (18%)</th>
                  <th rowSpan="3">TOTAL AMOUNT (Rs.)</th>
                </>
              )}
            </tr>
            <tr>
              <th rowSpan="2">Order</th>
              <th rowSpan="2">{title === 'PACKING COPY' ? 'Packed' : 'Produced'}</th>
              <th rowSpan="2">Order</th>
              <th rowSpan="2">{title === 'PACKING COPY' ? 'Packed' : 'Produced'}</th>
              {showJobWorkColumn && jobWorkStructure.map((item, index) => (
                <th key={`jobwork-header-${index}`} colSpan={item.type === 'group' ? item.colspan : 1} rowSpan={item.type === 'group' ? 1 : 2}>
                  {item.title}
                </th>
              ))}
            </tr>
            <tr>
              {showJobWorkColumn && jobWorkStructure.map((item, index) => (
                item.type === 'group' ? (
                  item.children.map((child, childIndex) => (
                    <th key={`jobwork-subheader-${index}-${childIndex}`}>{child.title}</th>
                  ))
                ) : null
              ))}
            </tr>
          </thead>
          <tbody>
            {workOrderData?.work_order_details?.length > 0 ? (
              workOrderData.work_order_details.map((item, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  <td>{item.die_profile?.die_number || 'N/A'}</td>
                  <td>
                    {item.die_profile?.die_diagram ? (
                      /\.(jpeg|jpg|png|gif|bmp)$/i.test(item.die_profile.die_diagram) ? (
                        <img
                          src={item.die_profile.die_diagram}
                          alt="Die Diagram"
                          className="section-image"
                          onError={(e) => {
                            if (!e.target.src.includes('placeholder.jpg')) {
                              e.target.src = '/static/images/placeholder.jpg';
                            }
                          }}
                        />
                      ) : /\.pdf$/i.test(item.die_profile.die_diagram) ? (
                        <a href={item.die_profile.die_diagram} target="_blank" rel="noopener noreferrer">
                          View PDF
                        </a>
                      ) : (
                        'Unsupported Format'
                      )
                    ) : '-'}
                  </td>
                  <td>{getDimensions(item)}</td>
                  <td>{item.weight_range || 'N/A'}</td>
                  <td>{item.length || '0'}</td>
                  <td>{item.pieces || '0'}</td>
                  <td>{'0'}</td>
                  <td>{item.net_weight ? Number(item.net_weight).toFixed(3) : '0.00'}</td>
                  <td>{'0.00'}</td>
                  <td>{getAlloyTemper(item)}</td>
                  {showJobWorkColumn && jobWorkColumns.map((column, columnIndex) => (
                    <td key={`jobwork-data-${columnIndex}`}>
                      {column.render(item[column.dataIndex], item)}
                    </td>
                  ))}
                  {['ACCOUNT/SALES COPY'].includes(title) && (
                    <>
                      <td>{getPrice(item)}</td>
                      <td>{item.packing_charge ? Number(item.packing_charge).toFixed(2) : '0.00'}</td>
                    </>
                  )}
                  {includePriceColumns && (
                    <>
                      <td>{item.total_basic_amount ? Number(item.total_basic_amount).toFixed(2) : '0.00'}</td>
                      <td>{item.gst_amount ? Number(item.gst_amount).toFixed(2) : '0.00'}</td>
                      <td>{item.total_amount ? Number(item.total_amount).toFixed(2) : '0.00'}</td>
                    </>
                  )}
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={totalColumns} style={{ textAlign: 'center', padding: '20px' }}>
                  No items available
                </td>
              </tr>
            )}
            {workOrderData?.work_order_details?.length > 0 && (
              <tr className="total-row">
                <td colSpan={totalRowFirstColSpan}>Total :</td>
                <td>{workOrderData?.total_pieces || '0'}</td>
                <td></td>
                <td>{workOrderData?.total_weight ? Number(workOrderData.total_weight).toFixed(3) : '0.000'}</td>
                <td></td>
                {!hasOnlyMillFinish ? (
                  <td colSpan={jobWorkColumns.length + 1}></td>
                ) : (
                  <td colSpan={jobWorkColumns.length - 1}></td>
                )}
                {['ACCOUNT/SALES COPY'].includes(title) && (
                  <>
                    <td></td>
                    <td></td>
                  </>
                )}
                {includePriceColumns && (
                  <>
                    <td>
                      {workOrderData?.work_order_details?.reduce((sum, item) => sum + (Number(item.total_basic_amount) || 0), 0).toFixed(2) || '0.00'}
                    </td>
                    <td>
                      {workOrderData?.work_order_details?.reduce((sum, item) => sum + (Number(item.gst_amount) || 0), 0).toFixed(2) || '0.00'}
                    </td>
                    <td>
                      {workOrderData?.work_order_details?.reduce((sum, item) => sum + (Number(item.total_amount) || 0), 0).toFixed(2) || '0.00'}
                    </td>
                  </>
                )}
              </tr>
            )}
          </tbody>
        </table>
        <div className="info-section new-info">
          <div style={{ flex: 1 }}>
            <strong>Supply Tolerance :</strong> {TOLERANCE_OPTIONS.find(opt => opt.value === workOrderData?.tolerance)?.label || 'N/A'}<br />
            <strong>Delivery Date :</strong> {workOrderData?.delivery_date ? dayjs(workOrderData.delivery_date).format('DD-MM-YYYY') : 'N/A'}<br />
            <strong>Quantity Control :</strong> As Per INDIAN STANDARDS (IS)<br />
            <strong>Remarks :</strong> {workOrderData?.remarks || 'N/A'}
          </div>
          <div style={{ flex: 1, textAlign: 'right' }}>
            <strong>Packing Type :</strong> {workOrderData?.packing_type?.length ? workOrderData.packing_type.join(', ') : 'N/A'}<br />
            <strong>Transporter Name :</strong> N/A<br />
            <strong>Vehicle No. :</strong> N/A
          </div>
        </div>
        <div className="signature-section">
          <div className="signature-box">Processed By<br />(Sales)</div>
          <div className="signature-box">Checked By<br />(Design Dept.)</div>
          <div className="signature-box">Approved By<br />(Sales Head)</div>
          <div className="signature-box">Approved By<br />(Director)</div>
        </div>
      </div>
    );
  };

  const commonStyles = `
        * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
            font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif, Apple Color Emoji, Segoe UI Emoji, Segoe UI Symbol, Noto Color Emoji !important;
        }
        body {
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji" !important;
            font-size: 16px;
            line-height: 1.4;
            color: #000;
        }
        .print-container {
            margin: 0 auto;
            padding: 10px;
        }
        .main-container {
            width: 100%;
            padding: 0;
        }
        .info-section {
            display: flex;
            justify-content: space-between;
            margin-bottom: 20px;
            font-size: 15px;
        }
        .info-left {
            flex: 1;
            max-width: 900px;
        }
        .info-right {
            flex: 1;
            text-align: right;
            max-width: 300px;
        }
        .info-table {
            border-collapse: collapse;
            width: 100%;
            font-size: 16px;
        }
        .info-table td {
            vertical-align: top;
        }
        .info-label {
            font-weight: bold;
        }
        .main-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 16px !important;
            margin-bottom: 20px;
        }
        .main-table th,
        .main-table td {
            border: 1px solid #dee2e6;
            padding: 6px 4px;
            text-align: center;
            vertical-align: middle;
        }
        .main-table th {
            background: transparent;
            color: black;
            font-weight: bold;
            font-size: 16px;
        }
        .main-table .section-image {
            width: 90px;
            height: 90px;
            object-fit: contain;
        }
        .total-row {
            font-weight: bold;
        }
        .signature-section {
            display: flex;
            justify-content: space-between;
            margin: 40px 0 20px;
            gap: 60px;
        }
        .signature-box {
            text-align: center;
            font-weight: bold;
            font-size: 16px;
        }
        .print-button {
            background: #12406a;
            color: white;
            border: none;
            padding: 10px 20px;
            font-size: 14px;
            border-radius: 4px;
            cursor: pointer;
            margin: 20px auto;
            display: block;
        }
        .page-break {
            display: none;
        }
    `;

  const printStyles = `
        @media print {
          .print-container {
              padding: 10px !important;
              box-sizing: border-box !important;
              width: 100% !important;
          }
          @page {
              margin: 0.5cm !important;
              size: A4;
          }
          html, body {
              margin: 0 !important;
              padding: 0 !important;
              width: 100% !important;
              height: auto !important;
              overflow: visible !important;
              background: white !important;
              font-size: 12px !important;
          }
          * {
              box-sizing: border-box !important;
          }
          .ant-row {
              width: 100% !important;
          }
          .ant-col {
              font-size: 12px !important;
          }
          .h1, .h2, .h3, .h4, .h5, .h6, h1, h2, h3, h4, h5, h6 {
              margin-bottom: 0.5rem;
              font-weight: 500;
              font-size: 14px;
              line-height: 1.2;
              color: inherit;
          }
          .main-heading {
              font-size: 20px !important;
          }
          .copy-label {
              font-size: 12px !important;
              float: right;
          }
          table {
              color: black;
              text-align: center;
              border-collapse: collapse;
              width: 100%;
          }
          .table-bordered {
              border: 1px solid #000 !important;
          }
          .table-bordered th,
          .table-bordered td {
              border: 1px solid #000 !important;
              padding: 4px !important;
              font-size: 10px !important;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
            .table-bordered thead th {
                border-bottom-width: 2px !important;
                font-weight: bold;
            }
            .main-table tbody tr {
                break-inside: avoid;
                break-after: auto;
            }
            .main-table .section-image {
                width: 60px !important;
                height: 60px !important;
            }
            .info-section {
                margin: 20px 0 !important;
                font-size: 12px !important;
            }
            .new-info {
                margin-top: 15px !important;
            }
            .signature-section {
                margin-top: 50px !important;
                font-size: 12px !important;
                gap: 40px !important;
            }
            .page-break {
                display: block !important;
                page-break-before: always;
                break-before: always;
                height: 0 !important;
                margin: 0 !important;
                padding: 0 !important;
            }
            .print-button {
                display: none !important;
            }
        }
    `;

  useEffect(() => {
    const styleEl = document.createElement('style');
    styleEl.innerHTML = commonStyles + printStyles;
    document.head.appendChild(styleEl);
    return () => document.head.removeChild(styleEl);
  }, []);

  if (!workOrderData) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '300px',
        }}
      >
        <div style={{ fontSize: '18px' }}>Loading work order data...</div>
      </div>
    );
  }

  const hasPermission = (permission) => {
    return currentUser?.permissions?.permissions?.workorder?.includes(permission) || false;
  };

  return (
    <div ref={contentRef} className="print-container">
      {hasPermission('print_production_copy') && renderCopy('PRODUCTION COPY')}
      {hasPermission('print_packing_copy') && (
        <>
          <div className="page-break" />
          {renderCopy('PACKING COPY')}
        </>
      )}
      {hasPermission('print_account_sales_copy') && (
        <>
          <div className="page-break" />
          {renderCopy('ACCOUNT/SALES COPY', isFixedPrice)}
        </>
      )}
      <button className="print-button" onClick={() => window.print()}>
        Print
      </button>
    </div>
  );
};

export default PrintWorkOrder2;